import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdCheck, MdContentCopy, MdSearch } from "react-icons/md";

import { ReactComponent as SoundLibraryIcon } from "../../../assets/soundLibrary.svg";
import courseServices from "../../../services/courseServices";
import { getLanguage } from "../../../utils/getLanguage";
import Button from "../../Button";
import CustomModal from "../../CustomModal";
import LoadingSpinner from "../../LoadingSpinner";
import Pagination from "../../Pagination";

const SoundLibrary = forwardRef(({}, ref) => {
  const lang = getLanguage();
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [sounds, setSounds] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(null);
  const [copiedSoundId, setCopiedSoundId] = useState(null);
  const [currentAudio, setCurrentAudio] = useState(null);

  useImperativeHandle(ref, () => ({
    openModal() {
      setIsOpen(true);
    },
    closeModal() {
      setIsOpen(false);
    },
  }));

  // Open modal → fetch categories
  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  // Debounce search input
  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // reset page when searching
    }, 500);
    return () => clearTimeout(delay);
  }, [searchTerm]);

  // Fetch sounds whenever category, page, search, sort, or lang changes
  useEffect(() => {
    if (selectedCategory) {
      fetchSounds(selectedCategory, currentPage, debouncedSearchTerm, sortOrder);
    }
  }, [selectedCategory, currentPage, debouncedSearchTerm, sortOrder, lang]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await courseServices.getSmoothySoundLibrary();
      setCategories(res || []);
      if (res.length > 0) {
        setSelectedCategory(res[0].id);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
    setLoading(false);
  };

  const fetchSounds = async (categoryId, page = 1, search = "", order = "asc") => {
    setLoading(true);
    try {
      const response = await courseServices.getSmoothySoundByID(categoryId, {
        per_page: 9,
        sort_by: "name",
        sort_dir: order,
        search,
        page,
      });

      setSounds(response?.data || []);
      setLastPage(response?.last_page || 0);
    } catch (error) {
      console.error("Error fetching images:", error);
    }
    setLoading(false);
  };

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleCopyPath = (sound) => {
    const textarea = document.createElement("textarea");
    textarea.value = sound.url;
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand("copy");
      setCopiedSoundId(sound.id);
      setTimeout(() => setCopiedSoundId(null), 2000);
    } catch (err) {
      console.error("Copy failed", err);
    }
    document.body.removeChild(textarea);
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleCloseModal}
      className={"max-w-[45rem]"}
      contentClassname="p-0 !py-0 !rounded-2xl"
    >
      <div className="flex max-h-[35rem]">
        {/* Categories */}
        <div className="custom-scrollbar-blue w-1/4 overflow-y-auto border-r bg-blue-500 px-1 py-4 dark:border-dark-border">
          <SoundLibraryIcon className="m-auto my-4 h-20 w-20 text-white" />
          <ul className="space-y-2">
            {categories.map((category) => (
              <li
                key={category.id}
                className={`cursor-pointer rounded px-3 py-2 text-sm capitalize text-white transition-colors duration-150 hover:bg-blue-600 dark:hover:bg-gray-900 ${
                  selectedCategory === category.id ? "bg-blue-700 font-medium text-blue-500" : ""
                }`}
                onClick={() => handleCategoryClick(category.id)}
              >
                {category.name}
              </li>
            ))}
          </ul>
        </div>

        {/* Sounds list */}
        <div className="custom-scrollbar w-3/4 overflow-y-auto p-4">
          <h3 className="mb-3 flex items-center gap-2 text-xl font-semibold text-blue-500">
            {t("sound_library")}
          </h3>

          {/* Search */}
          <div className="relative mb-4 flex w-full items-center justify-between rounded-xl bg-white dark:bg-[#1F2937]">
            <MdSearch
              style={{
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                // conditional left or right:
                [lang === "ar" ? "left" : "right"]: "0rem", // 'left-2' is 0.5rem
              }}
              className="h-8 w-8 rounded-xl bg-orange-500 p-1 text-xl text-white"
            />
            <input
              type="text"
              placeholder={t("search")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-md border-gray-300 bg-gray-100 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none dark:border-dark-border dark:bg-gray-800 dark:text-white"
            />
          </div>

          {/* Sounds Grid */}
          <div className="flex flex-1 flex-col justify-between gap-4">
            {loading ? (
              <LoadingSpinner />
            ) : sounds.length > 0 ? (
              <ul className="grid grid-cols-2 gap-2">
                {sounds.map((sound) => (
                  <li key={sound.id} className="rounded-lg bg-gray-100 p-3">
                    <p className="mb-2 font-light capitalize text-gray-600" title={sound.name}>
                      {sound.name}
                    </p>
                    <audio
                      controls
                      preload="none"
                      className="w-full"
                      src={`${process.env.REACT_APP_API_S3_URL}${sound.url}`}
                      onPlay={() => setCurrentAudio(sound)}
                      onPause={() => setCurrentAudio(null)}
                      controlsList="nodownload nofullscreen noplaybackrate"
                      disablePictureInPicture
                    />
                    <div className="mt-2 flex items-center justify-between">
                      <Button
                        label={t("copy_path")}
                        className="!px-1"
                        color="green"
                        variant="text"
                        iconLeft={
                          copiedSoundId === sound.id ? (
                            <MdCheck size={20} className="text-green-500" />
                          ) : (
                            <MdContentCopy size={20} />
                          )
                        }
                        onClick={() => handleCopyPath(sound)}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">{t("no_sounds_available")}</p>
            )}
          </div>

          {/* Pagination */}
          {lastPage > 1 && (
            <Pagination lastPage={lastPage} currentPage={currentPage} onPageChange={setCurrentPage} />
          )}
        </div>
      </div>
    </CustomModal>
  );
});

export default SoundLibrary;
